import { isArray, isEmpty } from '@/utils/is';
import ctt from '@/utils/const/index';
import { formatTime } from '@/utils/date';

import { translateTo } from '@/utils/locale';

export function getFormConfig(deviceType) {
  const key = matchKey(deviceType);

  const config = {
    options: getOptions(key),
    module: getModel(key),
    caseList: getCaseList(key)
  };

  return mergeCommonFormConfig(config);
}

const dict = {
  [ctt.RG_05]: 'nuclear',
  [ctt.CR2500]: 'cwa',
  [ctt.CD_603]: 'default',
  '^FCBR': 'bio'
};

export function matchKey(deviceType) {
  const keys = Object.keys(dict);

  const key = keys.find(e => new RegExp(e, 'i').test(deviceType));
  return dict[key] || 'default';
}

export function getTitle(deviceType) {
  const key = matchKey(deviceType);
  const dict = {
    nuclear: '核探测设备报警信息',
    bio: '生化监测设备报警信息',
    cwa: '化学检测设备报警信息',
    default: '设备报警信息'
  };
  return dict[key];
}

export function getCaseList(deviceType) {
  const caseDict = [
    { label: '转民警处理', value: 'policeHandling' },
    { label: '未找到可疑人', value: 'misreport' },
    { label: '劝离', value: 'dissuade' },
    { label: '自弃', value: 'abandonment' },
    { label: '视频查看无异常', value: 'vedioNoAbnormalities' },
    { label: '巡检无异常', value: 'inspectionNoAbnormalities' },
    { label: '现场已处置', value: 'siteDisposal' }
  ];
  const indeces = {
    nuclear: [0, 1, 6],
    cwa: [2, 3, 0, 1],
    bio: [4, 5],
    default: [0, 1, 2, 3, 4, 5]
  };

  const list = indeces[deviceType];

  if (!isArray(list)) return [];

  return list.map(index => caseDict[index]);
}

export function getOptions(deviceType) {
  const config = {
    nuclear: [
      { type: 'text', span: 12, prop: 'position', defaultValue: '--', label: '位置' },
      { type: 'text', span: 12, prop: 'typeName', defaultValue: '--', label: '类别' },
      { type: 'text', prop: 'message', defaultValue: '--', label: '剂量' }
    ],
    bio: [
      { type: 'text', span: 12, prop: 'position', defaultValue: '--', label: '位置' },
      { type: 'text', span: 12, prop: 'typeName', defaultValue: '--', label: '类别' },
      { type: 'text', span: 12, prop: 'message', defaultValue: '--', label: '气体' }
      // { type: 'text', span: 12, prop: 'concentration', defaultValue: '--', label: '浓度' }
    ],
    cwa: [
      { type: 'text', span: 12, prop: 'position', defaultValue: '--', label: '位置' },
      { type: 'text', span: 12, prop: 'message', defaultValue: '--', label: '物品' }
    ],
    default: [
      { type: 'text', span: 12, prop: 'position', defaultValue: '--', label: '位置' },
      { type: 'text', span: 12, prop: 'typeName', defaultValue: '--', label: '类别' }
    ]
  };
  return config[deviceType];
}

export function getModel(deviceType) {
  const model = {
    nuclear: {
      position: '',
      category: '',
      dosage: ''
    },
    bio: {
      position: '',
      category: '',
      gas: '',
      concentration: ''
    },
    cwa: {
      position: '',
      material: ''
    },
    default: {
      position: '',
      typeName: ''
    }
  };
  return model[deviceType];
}

export function mergeCommonFormConfig({ options, model, caseList }) {
  return {
    config: {
      labelWidth: 'auto',
      span: 24,
      options: [
        ...options,
        {
          type: 'text',
          prop: 'beginTime',
          span: 12,
          showFormat: v => formatTime(v) || '--',
          label: '报警开始时间'
        },
        {
          type: 'text',
          prop: 'endTime',
          span: 12,
          showFormat: v => formatTime(v) || '--',
          label: '报警结束时间'
        },
        {
          type: 'input',
          prop: 'dispositionTime',
          disabled: true,
          showFormat: v => formatTime(v),
          label: '报警确认时间'
        },
        { type: 'input', prop: 'operator', label: '报警处理人' },
        {
          span: 24,
          type: 'custom',
          slot: 'upload',
          prop: 'peoplePictures',
          label: '人员信息'
        },
        {
          span: 24,
          type: 'custom',
          slot: 'upload',
          prop: 'objectPictures',
          label: '实物照片'
        },
        {
          span: 24,
          type: 'custom',
          slot: 'upload',
          prop: 'devicePictures',
          label: '手持设备'
        },
        {
          type: 'radioGroup',
          prop: 'alarmType',
          dataKey: 'alarmTypeOptions',
          label: '报警种类'
        },
        {
          prop: 'dispositionSituation',
          label: '处理情况',
          children: [
            { type: 'checkboxGroup', dataKey: 'caseList', prop: 'dispositionSituation' },
            { type: 'input', attrs: { type: 'textarea' }, prop: 'dispositionInfo' }
          ]
        }
      ]
    },
    model: {
      ...model,
      beginTime: '',
      endTime: '',
      dispositionTime: Date.now(),
      operator: '',
      dispositionInfo: '',
      dispositionSituation: [],
      alarmType: 'normalAlarm',
      peoplePictures: [],
      devicePictures: [],
      objectPictures: []
    },
    dataSource: {
      caseList: [...caseList],
      alarmTypeOptions: [
        { label: '正常报警', value: 'normalAlarm' },
        { label: '演练报警', value: 'drillAlarm' },
        { label: '测试报警', value: 'testAlarm' }
      ]
    },
    rules: {
      operator: [
        {
          required: true,
          validator: (_rule, value, callback) => {
            const msg = translateTo('请填写处理人员', 'component.dispositionAlarm');

            if (isEmpty(value)) {
              callback(new Error(msg));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        }
      ],
      dispositionSituation: [
        {
          required: true,
          validator: (_rule, value, callback) => {
            const msg = translateTo('请选择处理情况', 'component.dispositionAlarm');

            if (isEmpty(value)) {
              callback(new Error(msg));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        }
      ]
    }
  };
}
